import React, { useRef } from 'react';
import AudioPlayer from 'react-h5-audio-player';
import styled, { css } from 'styled-components/macro';
import imagePlaceholder from '../../../svgs/image-placeholder.svg';
import { TeacherNavLink } from '../../common/elements/Nav';
import ProfileImage from '../../common/ProfileImage';
import {
  focusRing,
  fontSize,
  fontWeight,
  layout,
  lineHeight,
  radius,
  shadow,
  space,
  transition,
} from '../../../theme/tokens';

// Grid tracks below are in em (1em = 10px here, the root size) and size the
// avatar / delete / evidence columns, so they stay as they are.

export const MessageItem = styled.li`
  display: grid;
  grid-template-rows: 1.5em 2fr 1.5em min-content;
  grid-template-columns: 3.2em auto auto;
  grid-template-areas:
    '. created-at .'
    '. message-content message-content'
    'author-image message-content message-content'
    'author-image author author';
  padding: ${space[1]};
  align-self: flex-start;
  max-width: min(100%, ${layout.readableMaxWidth});
  ${({ authorIsStaff, isOwn }) =>
    authorIsStaff &&
    !isOwn &&
    css`
      ${MessageContent} {
        border: 2px solid ${({ theme }) => theme.classroomHighlight};
      }
      ${MessageCreatedByImage} {
        border: 2px solid ${({ theme }) => theme.classroomHighlight};
      }
    `}

  ${({ isOwn, isMessageDeletable }) =>
    !isOwn &&
    isMessageDeletable &&
    css`
      grid-template-areas:
        '. created-at .'
        'delete-message message-content message-content'
        'author-image message-content message-content'
        'author-image author author';
      ${DeleteMessageButton} {
        text-align: right;
      }
    `}

    ${({ isOwn, isEvidencable }) =>
      !isOwn &&
      isEvidencable &&
      css`
        grid-grid-template-columns: 3.2em auto auto 4em;
        grid-template-areas:
          '. created-at . .'
          'delete-message message-content message-content evidence'
          'author-image message-content message-content evidence'
          'author-image author author author';
      `}

  ${({ isOwn }) =>
    isOwn &&
    css`
      grid-template-columns: 4em auto auto min-content;
      grid-template-areas:
        '. author author .'
        'evidence message-content message-content delete-message'
        'evidence message-content message-content delete-message'
        '. . created-at .';
      align-self: flex-end;
      ${MessageContent} {
        border-radius: ${radius.lg} ${radius.lg} ${radius.sm} ${radius.lg};
        justify-self: end;
        color: ${({ theme }) => theme.textLight};
        background: ${({ theme }) => theme.userMessage};
      }
      ${MessageCreatedBy} {
        justify-self: end;
        margin-right: ${space[2]};
      }
      ${MessageCreatedAt} {
        justify-self: end;
      }
      ${DeleteButton} {
        text-align: right;
        display: inline-block;
      }
    `}

    ${({ isPreview }) =>
      isPreview &&
      css`
        grid-template-columns: 5em 1fr;
        grid-template-areas:
          '. created-at'
          'delete-file message-content'
          '. message-content'
          '. author';
      `}

    ${({ isEvidencePreview }) =>
      isEvidencePreview &&
      css`
        grid-template-columns: 5em 1fr;
        grid-template-areas:
          'created-at created-at'
          'message-content message-content'
          'message-content message-content'
          '. author';
      `}

      ${({ isOwn, isEvidencePreview }) =>
        !isOwn &&
        isEvidencePreview &&
        css`
          grid-grid-template-columns: auto auto;
          grid-template-areas:
            'created-at created-at '
            'message-content message-content'
            'message-content message-content'
            'author author';
        `}

    /* audio bubbles are rounder so they hug the round player controls */
    ${({ isAudio, isOwn }) =>
      isAudio &&
      (isOwn
        ? css`
            ${MessageAudio} {
              border-radius: 2.8rem 2.8rem ${radius.sm} 2.8rem;
            }
          `
        : css`
            ${MessageAudio} {
              border-radius: 2.8rem 2.8rem 2.8rem ${radius.sm};
            }
          `)}

    ${({ isAudio, isOwn, hasText }) =>
      isAudio &&
      hasText &&
      (isOwn
        ? css`
            ${MessageContent} {
              border-radius: 2.5rem 2.5rem ${radius.sm} 2.5rem;
            }
          `
        : css`
            ${MessageContent} {
              border-radius: 2.5rem 2.5rem 2.5rem ${radius.sm};
            }
          `)}

    ${({ isAudio, isOwn, hasText }) =>
      isAudio &&
      !hasText &&
      (isOwn
        ? css`
            ${MessageContent} {
              border-radius: 3rem 3rem ${radius.sm} 3rem;
            }
          `
        : css`
            ${MessageContent} {
              border-radius: 3rem 3rem 3rem ${radius.sm};
            }
          `)}
`;

export const MessageContent = styled.div`
  grid-area: message-content;
  margin: 0;
  background: ${({ theme }) => theme.incomingMessage};
  border-radius: ${radius.lg} ${radius.lg} ${radius.lg} ${radius.sm};
  box-shadow: ${shadow.sm};
  justify-self: start;
  align-self: end;
  line-height: 0;
  padding: ${space[1]};
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
  min-width: 0;
  max-width: 100%;
`;

export const MessageText = styled.p`
  margin: 0;
  padding: ${space[1]} ${space[3]};
  font-weight: ${fontWeight.medium};
  font-size: ${fontSize.md};
  line-height: ${lineHeight.normal};
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: pre-line;

  ${({ fileType }) =>
    fileType &&
    (fileType === 'audio'
      ? css`
          padding: 0 ${space[5]} ${space[1]};
        `
      : css`
          padding: 0 ${space[3]} ${space[1]};
        `)}
`;

const MsgImg = styled.img`
  border-radius: ${radius.md};
  /* media preview size */
  max-height: 21rem;
  max-width: 100%;
  object-fit: contain;
`;

export const MessageImage = (props) => {
  const img = useRef(null);
  return (
    <MsgImg
      {...props}
      ref={img}
      onError={() => {
        if (img.current) {
          img.current.src = imagePlaceholder;
        }
      }}
    />
  );
};

export const MessageVideo = styled.video`
  max-height: 21rem;
  max-width: 100%;
  border-radius: ${radius.md};
`;

export const MessageAudio = styled(AudioPlayer)`
  /* player width, still fits a 390px phone next to the avatar column */
  min-width: 21rem;
  padding: ${space[1]} ${space[2]};
`;

export const MessageCreatedBy = styled.p`
  color: ${({ theme }) => theme.subHeaderColour};
  grid-area: author;
  margin: 0;
  align-self: end;
  padding: ${space[1]} 0;
  font-weight: ${fontWeight.semibold};
  font-size: ${fontSize.xs};
  line-height: ${lineHeight.tight};
  overflow-wrap: anywhere;
`;

export const MessageCreatedAt = styled.p`
  color: ${({ theme }) => theme.subHeaderColour};
  grid-area: created-at;
  align-self: end;
  margin: 0;
  padding-bottom: ${space[1]};
  font-size: ${fontSize.xs};
  line-height: ${lineHeight.tight};
  white-space: nowrap;
`;

export const MessageCreatedByImage = styled(ProfileImage)`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  grid-area: author-image;
  align-self: center;
  justify-self: center;
  object-fit: fill;
`;

const iconButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${layout.touchTarget};
  min-height: ${layout.touchTarget};
  padding: 0;
  border: none;
  border-radius: ${radius.pill};
  background: none;
  color: ${({ theme }) => theme.textDark};
  line-height: 1;
  cursor: pointer;
  transition: background ${transition.fast}, color ${transition.fast};
  &:hover {
    background: ${({ theme }) => theme.hoverFill};
    color: ${({ theme }) => theme.subHeaderColour};
  }
  ${focusRing}
`;

export const DeleteButton = styled.button`
  ${iconButton}
  grid-area: delete-file;
  align-self: start;
  justify-self: start;
`;

export const DeleteMessageButton = styled.button`
  ${iconButton}
  grid-area: delete-message;
  align-self: center;
  justify-self: center;
`;

const inlineLink = css`
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 0.2em;
  font-weight: ${fontWeight.bold};
  font-size: ${fontSize.md};
  overflow-wrap: anywhere;
  &:hover {
    text-decoration-thickness: 2px;
  }
  ${focusRing}
`;

export const InvisibleExternalLink = styled.button`
  ${inlineLink}
  padding: 0;
  background: none;
  border: none;
  font-family: inherit;
  line-height: inherit;
  text-align: left;
  cursor: pointer;
`;

export const InvisibleInternalLink = styled(TeacherNavLink)`
  ${inlineLink}
`;

export * from '../../common/elements/Students.js';
